import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { isAdmin, type ChannelWithMembership } from '@avslack/shared';
import { browseChannels } from '../lib/api';
import { useJoinChannel } from '../data/channels';
import { useSession } from '../lib/session';
import { Dialog, useAnimatedClose } from './Dialog';

/** Ekezet-fuggetlen egyezes. */
const fold = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');

/**
 * Csatorna-bongeszo.
 *
 * A publikus csatornak elonezettel: latszik a tema es a tagletszam, es
 * csatlakozas nelkul is meg lehet nyitni. Ez a "mi folyik a haznal" kerdesre
 * ad valaszt anelkul, hogy mindenbe be kellene lepni.
 */
export function ChannelBrowser({ onClose: closeNow }: { onClose: () => void }) {
  const onClose = useAnimatedClose(closeNow);
  const navigate = useNavigate();
  const joinChannel = useJoinChannel();
  const session = useSession();
  const me = session.data?.user;

  /**
   * BELEPNI NEM LEHET, csak felvenni.
   *
   * A szerver a csatlakozast is kezeloi joghoz koti (`requireManageAccess`):
   * admin vagy a csatorna letrehozoja. Egy sima tag nem lephet be magatol -
   * a tagsag dontes, nem onkiszolgalas.
   *
   * A gomb eddig mindenkinek ott volt, es kattintasra hibauzenettel allt meg.
   * Egy gomb, ami mindig elutasit, rosszabb, mintha ott sem lenne: azt
   * tanitja, hogy a felulet nem mond igazat.
   */
  const canJoin = (channel: ChannelWithMembership): boolean =>
    Boolean(me && (isAdmin(me) || channel.createdBy === me.id));
  const [query, setQuery] = useState('');

  const { data: channels, isLoading } = useQuery({
    queryKey: ['channels', 'browse'],
    queryFn: browseChannels,
    staleTime: 30_000,
  });

  const filtered = useMemo(() => {
    const needle = fold(query.trim());
    const list = channels ?? [];
    const matching = needle
      ? list.filter(
          (c) => fold(c.name ?? '').includes(needle) || fold(c.topic ?? '').includes(needle),
        )
      : list;

    // Amiben meg nem vagyok tag, az elorebb: az a felfedezni valo.
    return [...matching].sort((a, b) => {
      if (a.membership.isMember !== b.membership.isMember) return a.membership.isMember ? 1 : -1;
      return b.memberCount - a.memberCount;
    });
  }, [channels, query]);

  const open = (channel: ChannelWithMembership) => {
    onClose();
    void navigate({ to: '/c/$channelId', params: { channelId: channel.id } });
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => !next && onClose()}
      title="Csatornák böngészése"
      description="A nyilvános csatornákat belépés nélkül is megnézheted. A tagságot admin vagy a csatorna létrehozója adja."
      width="lg"
    >
      <div className="mt-4">
        <input
          aria-label="Csatorna keresése"
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Keress névre vagy témára…"
          className="w-full rounded-control border border-border-soft bg-surface px-3 py-2 text-base outline-none placeholder:text-ink-faint focus:border-accent"
        />

        <ul className="mt-3 max-h-[calc(var(--app-height,100dvh)*0.45)] space-y-1 overflow-y-auto">
          {isLoading && <li className="py-6 text-center text-sm text-ink-muted">Betöltés…</li>}

          {!isLoading && filtered.length === 0 && (
            <li className="py-8 text-center text-sm text-ink-muted">
              {query.trim() ? (
                <>
                  Nincs találat erre: <strong className="text-ink">{query.trim()}</strong>
                </>
              ) : (
                'Még nincs nyilvános csatorna, amit megnézhetnél.'
              )}
            </li>
          )}

          {filtered.map((channel) => (
            <li
              key={channel.id}
              className="flex items-center gap-3 rounded-control border border-border-soft px-3 py-2.5 transition-colors duration-(--duration-quick) hover:border-border-strong"
            >
              <button
                type="button"
                onClick={() => open(channel)}
                className="min-w-0 flex-1 text-left"
              >
                <span className="flex items-center gap-1.5">
                  <span aria-hidden="true" className="text-ink-faint">
                    #
                  </span>
                  <span className="truncate font-semibold">{channel.name}</span>
                  <span className="shrink-0 text-2xs text-ink-faint">
                    {channel.memberCount} tag
                  </span>
                </span>
                {channel.topic && (
                  <span className="mt-0.5 block truncate text-xs text-ink-muted">
                    {channel.topic}
                  </span>
                )}
              </button>

              {channel.membership.isMember ? (
                <span className="shrink-0 rounded-pill bg-surface-sunken px-2.5 py-1 text-2xs font-medium text-ink-muted">
                  tag vagy
                </span>
              ) : canJoin(channel) ? (
                <button
                  type="button"
                  onClick={() => joinChannel.mutate(channel.id)}
                  disabled={joinChannel.isPending}
                  className="shrink-0 rounded-control bg-accent px-3 py-1.5 text-xs font-semibold text-accent-ink transition-colors duration-(--duration-quick) hover:bg-accent-hover disabled:opacity-50"
                >
                  Csatlakozás
                </button>
              ) : (
                <span
                  title="A tagságot admin vagy a csatorna létrehozója adja."
                  className="shrink-0 rounded-pill bg-surface-sunken px-2.5 py-1 text-2xs font-medium text-ink-faint"
                >
                  meghívásra
                  <span className="sr-only">
                    {' '}
                    – a tagságot admin vagy a csatorna létrehozója adja.
                  </span>
                </span>
              )}
            </li>
          ))}
        </ul>

        {joinChannel.isError && (
          <p role="alert" className="mt-3 text-xs text-danger">
            {joinChannel.error?.message || 'A csatlakozás nem sikerült. Próbáld újra.'}
          </p>
        )}
      </div>
    </Dialog>
  );
}
